"use client";

import { Bot, CheckCircle, MonitorPause } from "lucide-react";

import { UserDto } from "../../dto/user.dto";
import { ArticleDto } from "../../dto/article.dto";
import ProgressCircle from "../progress/ProgressCircle";

type TimerCircleProps = {
  user: UserDto | null;
  article: ArticleDto | null;
  time?: number;
  totalTime?: number;
  isPaused?: boolean;
  isCompleted?: boolean;
  onClick: () => void;
};

const TimerCircle: React.FC<TimerCircleProps> = (props) => {
  const showProgress =
    !!props.user &&
    !!props.article &&
    !props.article.isReaded &&
    !props.isCompleted &&
    typeof props.time === "number" &&
    typeof props.totalTime === "number" &&
    props.totalTime > 0;

  const progress = showProgress
    ? Math.min(props.time! / props.totalTime!, 1)
    : undefined;

  // Calculate remaining time for countdown
  const remainingTime = showProgress
    ? Math.max(0, Math.ceil(props.totalTime! - props.time!))
    : 0;

  const getTailwindColor = (cssVar: string) =>
    getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();

  const color = !props.user
    ? getTailwindColor("--color-primary")
    : props.article?.isReaded || props.isCompleted
    ? getTailwindColor("--color-success")
    : props.isPaused
    ? getTailwindColor("--color-warning")
    : getTailwindColor("--color-primary");

  const bgColor = getTailwindColor("--color-background");

  const formatTime = (seconds: number): string => {
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    }
    return `${seconds}s`;
  };

  return (
    <div className="z-40 cursor-pointer" onClick={props.onClick}>
      <ProgressCircle
        {...(showProgress ? { progress } : {})}
        color={color}
        bgColor={bgColor}
        transition={true}
      >
        {!props.user ? (
          <Bot className="animate-bounce" style={{ color: color }} />
        ) : props.article?.isReaded || props.isCompleted ? (
          <CheckCircle
            className="w-6 h-6"
            style={{
              color: "var(--color-success)",
            }}
          />
        ) : props.isPaused ? (
          <div className="flex flex-col items-center">
            <MonitorPause
              className="w-4 h-4"
              style={{
                color: "var(--color-warning)",
              }}
            />
            {showProgress && (
              <span
                className="text-xs"
                style={{
                  color: "var(--color-warning)",
                }}
              >
                {formatTime(remainingTime)}
              </span>
            )}
          </div>
        ) : showProgress ? (
          <div className="flex flex-col items-center">
            <span className="text-xs text-primary font-medium">
              {formatTime(remainingTime)}
            </span>
          </div>
        ) : (
          <Bot className="animate-bounce" style={{ color: color }} />
        )}
      </ProgressCircle>
    </div>
  );
};

export default TimerCircle;
